"use client";

import { useRouter } from "next/navigation";
import {
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import { PALETTE, AXIS_STYLE, TOOLTIP_STYLE } from "@/lib/recharts-theme";
import { formatMoney } from "@/lib/format";
import type { NicheRanking } from "@/lib/types";

type Props = {
  niches: NicheRanking[];
};

type ChartPoint = {
  name: string;
  value: number;
  category: string;
  totalMrr: number;
};

export function MrrDistributionChart({ niches }: Props) {
  const router = useRouter();

  const sorted = [...niches]
    .sort((a, b) => b.totalMrr - a.totalMrr)
    .slice(0, 12);

  if (sorted.length === 0) {
    return (
      <div className="chart-card">
        <h3 className="card-title">Revenue distribution by niche</h3>
        <p className="card-desc">Where verified recurring revenue is concentrated</p>
        <div className="chart-placeholder">No data to display</div>
      </div>
    );
  }

  const totalMrr = sorted.reduce((sum, n) => sum + n.totalMrr, 1);
  const data: ChartPoint[] = sorted.map((n) => ({
    name: n.label,
    value: (n.totalMrr / totalMrr) * 100,
    category: n.category,
    totalMrr: n.totalMrr,
  }));

  return (
    <div className="chart-card">
      <h3 className="card-title">Revenue distribution by niche</h3>
      <p className="card-desc">Click a bar or category name to inspect that niche</p>
      <div className="chart-container">
        <ResponsiveContainer width="100%" height="100%">
          <BarChart data={data} layout="vertical" margin={{ right: 20, left: 4 }}>
            <CartesianGrid strokeDasharray="3 3" opacity={0.3} horizontal={false} />
            <XAxis
              type="number"
              domain={[0, 100]}
              tick={AXIS_STYLE}
              tickFormatter={(v) => `${Math.round(v)}%`}
            />
            <YAxis
              type="category"
              dataKey="name"
              width={120}
              tick={AXIS_STYLE}
              tickLine={false}
            />
            <Tooltip
              {...TOOLTIP_STYLE}
              cursor={{ fill: "rgba(139, 92, 246, 0.1)" }}
              formatter={() => null}
              content={({ active, payload }) => {
                if (!active || !payload?.[0]) return null;
                const p = payload[0].payload as ChartPoint;
                return (
                  <div
                    style={{
                      ...TOOLTIP_STYLE.contentStyle,
                      padding: "12px 14px",
                    }}
                  >
                    <div style={TOOLTIP_STYLE.labelStyle}>
                      {p.value.toFixed(1)}% | {formatMoney(p.totalMrr)}/mo
                    </div>
                  </div>
                );
              }}
            />
            <Bar
              dataKey="value"
              radius={[0, 4, 4, 0]}
              maxBarSize={28}
              onClick={(item) => {
                const payload = item?.payload as ChartPoint | undefined;
                if (payload) router.push(`/dashboard/${payload.category}`);
              }}
              cursor="pointer"
            >
              {data.map((_, index) => (
                <Cell key={index} fill={PALETTE[index % PALETTE.length]} />
              ))}
            </Bar>
          </BarChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
}
